/**
 * Market data freshness.
 *
 * The market-data-updater POSTs a completion event to the backend when a sync finishes; the
 * backend records it and bumps a revision counter. This module lets the UI notice that a
 * sync completed and reload charts, instead of the customer having to guess whether today's
 * bars have landed and refresh the page by hand.
 *
 * The poll is deliberately cheap: /api/market-data/sync-status is public, needs no auth, and
 * the only value that matters is the revision number. Charts are only reloaded when it moves.
 */

import { useEffect, useRef, useState } from 'react';

const SYNC_STATUS_ENDPOINT = '/api/market-data/sync-status';

/** Default gap between polls. Data syncs are nightly, so this need not be aggressive. */
const DEFAULT_POLL_INTERVAL_MS = 120_000;

/**
 * Identity of the sync a payload describes.
 *
 * NOT the revision counter, which is the obvious choice and the wrong one. `revision` lives in
 * backend instance memory (`private volatile long`), so it resets to 0 whenever the backend
 * restarts — every deploy, every Cloud Run revision change. Keyed on that, a restart looks
 * exactly like a fresh sync to every connected browser at once, and they all reload their charts
 * for data that did not change.
 *
 * `finishedAt` comes from the sync event the updater persists to storage, so it survives
 * restarts and only moves when a sync genuinely completed. Revision is kept as a fallback for
 * the case where no sync has ever run and there is no event to read.
 */
export const syncIdentity = (data) => {
  if (!data) return null;
  const event = data.status || Object.values(data.markets || {})[0] || null;
  if (event && event.finishedAt) return `finishedAt:${event.finishedAt}`;
  if (typeof data.revision !== 'undefined') return `revision:${data.revision}`;
  return null;
};

/** Fetches the current freshness state, or null if it cannot be read. */
export const fetchSyncStatus = async (api, market) => {
  if (!api) {
    return null;
  }
  try {
    const url = market ? `${SYNC_STATUS_ENDPOINT}?market=${encodeURIComponent(market)}` : SYNC_STATUS_ENDPOINT;
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.debug('Could not read market data sync status:', error?.message || error);
    return null;
  }
};

/**
 * Watches for completed market data syncs.
 *
 * @param {object}   api             axios instance from AuthContext
 * @param {object}   options
 * @param {string}   options.market  optional market code ('US' | 'IN')
 * @param {number}   options.intervalMs
 * @param {Function} options.onSyncComplete called with the event when a new sync lands
 *
 * @returns {{ status: object|null, lastSyncedAt: string|null, isStale: boolean }}
 */
export const useMarketDataSync = (api, { market, intervalMs = DEFAULT_POLL_INTERVAL_MS, onSyncComplete } = {}) => {
  const [status, setStatus] = useState(null);

  // Identity of the last sync we reported. Held in a ref so the polling effect does not
  // re-subscribe every time it changes, which would restart the interval on each tick.
  const lastIdentityRef = useRef(null);
  const onSyncCompleteRef = useRef(onSyncComplete);

  // Keep the latest callback without making it a dependency of the interval.
  useEffect(() => {
    onSyncCompleteRef.current = onSyncComplete;
  }, [onSyncComplete]);

  useEffect(() => {
    if (!api) {
      return undefined;
    }

    let cancelled = false;

    const poll = async () => {
      const data = await fetchSyncStatus(api, market);
      if (cancelled || !data) {
        return;
      }

      setStatus(data);

      const identity = syncIdentity(data);
      if (identity === null) {
        return;
      }

      if (lastIdentityRef.current === null) {
        // First reading establishes the baseline; it is not a "new sync".
        lastIdentityRef.current = identity;
        return;
      }

      if (identity !== lastIdentityRef.current) {
        lastIdentityRef.current = identity;
        const event = data.status || Object.values(data.markets || {})[0] || null;
        if (onSyncCompleteRef.current) {
          onSyncCompleteRef.current(event, data);
        }
      }
    };

    poll();
    const timer = setInterval(poll, intervalMs);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [api, market, intervalMs]);

  const event = status?.status || Object.values(status?.markets || {})[0] || null;
  const lastSyncedAt = event?.finishedAt || null;

  return {
    status,
    lastSyncedAt,
    /** True when the most recent sync did not complete cleanly. */
    isStale: Boolean(event && event.status && event.status !== 'SUCCESS'),
  };
};

export default useMarketDataSync;
